/**
 * BigQuery event-logging helpers for the single-order header.
 *
 * Each function enqueues an analytics event via the API worker manager
 * for actions such as ICD editing, mark-as-completed, NAR RPA trigger,
 * and auth-on-file refresh.
 * @module headerEventLogger
 */
import { User } from "../../../data-model/firebaseAuthModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { checkIfBusinessOfficeNotesAreEmpty } from "../../../utils/busniessOfficeNotesHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import { getAllJCodesFromAuthOnFile } from "./existingDocsToLs";

interface LogPrimaryIcdEditButtonClickedParams {
  generalOrderDetails?: GeneralOrderDetails;
  coverageDetails?: SingleCoverage[];
  authOnFile?: AuthOnFile;
  user?: User | null;
  primaryIcdCodes: string[];
  jcode_icd: { jCode: string; icd_state: string }[];
  submissionData?: Submission;
}

interface LogMarkAsCompletedButtonSavedParams {
  generalOrderDetails?: GeneralOrderDetails;
  authOnFile?: AuthOnFile;
  coverageDetails?: SingleCoverage[];
  user?: User | null;
  oldAuthStatus: string | undefined;
  modalName:
    | ModalNamesForBigQuery.HAPPY_FLOW_MODAL
    | ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW;
  submissionData?: Submission;
}

interface LogTriggerRpaClickedNarParams {
  generalOrderDetails?: GeneralOrderDetails;
  user?: User | null;
  payerName: string;
}

interface LogRefreshButtonClickedParams {
  generalOrderDetails?: GeneralOrderDetails;
  user?: User | null;
  payerName: string;
}

/**
 * Logs the PRIMARY_ICD_EDIT_BUTTON_CLICKED event
 */
export const logPrimaryIcdEditButtonClicked = ({
  generalOrderDetails,
  coverageDetails,
  authOnFile,
  user,
  primaryIcdCodes,
  jcode_icd,
  submissionData,
}: LogPrimaryIcdEditButtonClickedParams) => {
  const orgId = getOrgIdForFetchExternalWorklist();
  apiWorkerManager.enqueueAndProcess({
    patient_id:
      generalOrderDetails?.demographics?.patient_id ??
      generalOrderDetails?.demographics?.patient_mrn ??
      "",
    order_id: generalOrderDetails?.order_id ?? "",
    org_id: orgId ?? "",
    event_name: EventsName.PRIMARY_ICD_EDIT_BUTTON_CLICKED,
    user_id: user?.id ?? "",
    user_email: user?.email ?? "",
    additional_data: {
      screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
      payer_name:
        coverageDetails?.find(
          (coverage) => coverage.coverage_type === "primary",
        )?.payer_name ?? "",
      assigned_primary_icd_code: primaryIcdCodes,
      j_code: (getAllJCodesFromAuthOnFile(authOnFile ?? undefined) ?? [])
        ?.map((jCode) => jCode.j_code)
        .join(","),
      jcode_icd,
      //TODO: Not sure from where to get this data from
      regimen_icd_state: submissionData?.regimen_icd_state ?? "",
    },
  });
};

/**
 * Logs the MARK_AS_COMPLETED_BUTTON_SAVED event
 */
export const logMarkAsCompletedButtonSaved = ({
  generalOrderDetails,
  authOnFile,
  coverageDetails,
  user,
  oldAuthStatus,
  modalName,
  submissionData,
}: LogMarkAsCompletedButtonSavedParams) => {
  const orgId = getOrgIdForFetchExternalWorklist();
  apiWorkerManager.enqueueAndProcess({
    patient_id:
      generalOrderDetails?.demographics?.patient_id ??
      generalOrderDetails?.demographics?.patient_mrn ??
      "",
    order_id: generalOrderDetails?.order_id ?? "",
    org_id: orgId ?? "",
    event_name: EventsName.MARK_AS_COMPLETED_BUTTON_SAVED,
    user_id: user?.id ?? "",
    user_email: user?.email ?? "",
    additional_data: {
      screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
      modal_name: modalName,
      master_auth_status_old: oldAuthStatus ?? "",
      master_auth_status_new: authOnFile?.auth_status ?? "",
      master_auth_status_changed:
        (oldAuthStatus ?? "") === (authOnFile?.auth_status ?? "")
          ? "false"
          : "true",
      payer_name:
        coverageDetails?.find(
          (coverage) => coverage.coverage_type === "primary",
        )?.payer_name ?? "",
      plan_name:
        coverageDetails?.find(
          (coverage) => coverage.coverage_type === "primary",
        )?.plan_name ?? "",
      j_code: (getAllJCodesFromAuthOnFile(authOnFile ?? undefined) ?? [])
        ?.map((jCode) => jCode?.j_code ?? "")
        .join(","),
      bo_empty: checkIfBusinessOfficeNotesAreEmpty(
        authOnFile?.reports ?? [],
      ).toString(),
      regimen_icd_state: submissionData?.regimen_icd_state ?? "",
    },
  });
};

/**
 * Logs the TRIGGER_RPA_CLICKED_NAR event
 */
export const logTriggerRpaClickedNar = ({
  generalOrderDetails,
  user,
  payerName,
}: LogTriggerRpaClickedNarParams) => {
  const orgId = getOrgIdForFetchExternalWorklist();
  apiWorkerManager.enqueueAndProcess({
    patient_id:
      generalOrderDetails?.demographics?.patient_id ??
      generalOrderDetails?.demographics?.patient_mrn ??
      "",
    order_id: generalOrderDetails?.order_id ?? "",
    org_id: orgId ?? "",
    event_name: EventsName.TRIGGER_RPA_CLICKED_NAR,
    user_id: user?.id ?? "",
    user_email: user?.email ?? "",
    additional_data: {
      screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
      payer_name: payerName,
    },
  });
};

/**
 * Logs the REFRESH_BUTTON_CLICKED event
 */
export const logRefreshButtonClicked = ({
  generalOrderDetails,
  user,
  payerName,
}: LogRefreshButtonClickedParams) => {
  const orgId = getOrgIdForFetchExternalWorklist();
  apiWorkerManager.enqueueAndProcess({
    patient_id:
      generalOrderDetails?.demographics?.patient_id ??
      generalOrderDetails?.demographics?.patient_mrn ??
      "",
    order_id: generalOrderDetails?.order_id ?? "",
    org_id: orgId ?? "",
    event_name: EventsName.REFRESH_BUTTON_CLICKED + " (Auth Status Screen)",
    user_id: user?.id ?? "",
    user_email: user?.email ?? "",
    additional_data: {
      screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
      source: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
      payer_name: payerName,
    },
  });
};
